import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useAvailableIndications } from "../hooks/useAvailableIndications";
import { useClearEnrollingConditionSet } from "../hooks/useClearEnrollingConditionSet";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { InitialRequestConfig } from "../models/flexible/initial-request";
import { isStartingPoint } from "../models/flexible/typed-flex-entity";
import { FlexFormStatus, Indication, Maybe } from "../models/generated/graphql";
import { indicationDisplay } from "../utils/base-agreement-formatters";
import { isEmptyValue } from "../utils/is-empty-value";
import { GeneralFieldController } from "./general-field-controller";
import { GET_PATIENT_ENTITIES_BY_ENTITY_ID, GET_PATIENT_ENTITIES_BY_ID, GET_SCOPES, whereDrugs } from "./queries";

function compare(a: { code: string }, b: { code: string }) {
	return a.code === b.code;
}

export class IndicationFieldController extends GeneralFieldController {
	getInitialRequestComponents?(): InitialRequestConfig {
		const { entityId = "", patientId = this.parent.context.flexEntity?.data[AttributeIdentifiers.PatientId] } =
			this.parent.context.parameters;

		if (entityId) {
			return {
				queries: [GET_PATIENT_ENTITIES_BY_ENTITY_ID, GET_SCOPES],
				variables: {
					entityId,
					where: whereDrugs(
						this.parent.context.entityType.id,
						this.parent.context.isNegotiable ? this.parent.context.cases[0].sourceTenantId : this.parent.context.tenantId
					),
				},
			};
		}

		if (patientId) {
			return {
				queries: [GET_PATIENT_ENTITIES_BY_ID, GET_SCOPES],
				variables: {
					patientId,
					where: whereDrugs(
						this.parent.context.entityType.id,
						this.parent.context.isNegotiable ? this.parent.context.cases[0].sourceTenantId : this.parent.context.tenantId
					),
				},
			};
		}

		return { queries: [], variables: {} };
	}

	override mapInitialValue(value: unknown): unknown {
		if (value || !this.parent.context.cases.length) {
			return super.mapInitialValue(value);
		}

		const caseInstance = this.parent.context.cases[0];
		const indication = caseInstance.flexEntities.find((entity) => entity.data[AttributeIdentifiers.Indication])?.data[
			AttributeIdentifiers.Indication
		];
		return super.mapInitialValue(indication);
	}

	override mapFormValue(value: unknown): unknown {
		if (isEmptyValue(value)) {
			return null;
		}

		return typeof value === "string" ? { type: value, code: value } : value;
	}

	override useControlState(): ControlState {
		if (this.parent.context.flexEntity?.status === FlexFormStatus.Submitted && isStartingPoint(this.parent.context.flexEntity)) {
			return "enabled";
		}

		return this.parent.context.cases?.length && this.parent.context.startingPoint?.data[AttributeIdentifiers.Indication]
			? "disabled"
			: "enabled";
	}

	override useControlProps(): Maybe<FlexControlProps> | undefined {
		const { options, isIndicationConditionExcluded, isFreeText } = useAvailableIndications(this);
		const { t } = useTranslation();
		const handleClear = useClearEnrollingConditionSet(this.parent.context);

		return {
			autoSelect: false,
			controlType: "typeahead",
			options,
			freeSolo: isIndicationConditionExcluded || isFreeText,
			property: "code",
			isChildInFormik: true,
			getOptionLabel: (option: Indication) => (typeof option === "string" ? option : indicationDisplay(option, t)),
			onClear: isIndicationConditionExcluded || isFreeText || options.length > 1 ? undefined : handleClear,
			getOptionSelected: compare,
			groupBy: (option: any) => option.group,
		};
	}

	override getValueTypeProps() {
		return {
			controlType: "typeahead",
			property: "code",
		};
	}

	protected override createFieldValidationRuleByDataType(): Yup.AnySchema {
		return Yup.mixed().nullable();
	}
}
